import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { DescriptionSection, DescriptionText, EnergyParticles, EnergyParticle, HighlightedWord } from '../../components/ThankYouStyles';

// Animações
const scanlineEffect = keyframes`
  0% {
    background-position: 0 0;
    opacity: 0.2;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    background-position: 0 100%;
    opacity: 0.2;
  }
`;

const glitchEffect = keyframes`
  0% {
    transform: translate(0);
    text-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
  }
  2% {
    transform: translate(-2px, 1px);
    text-shadow: -3px 0 2px rgba(100, 182, 255, 0.8), 3px 0 2px rgba(0, 255, 255, 0.8);
  }
  4% {
    transform: translate(2px, -1px);
    text-shadow: 3px 0 2px rgba(100, 182, 255, 0.8), -3px 0 2px rgba(0, 255, 255, 0.8);
  }
  5% {
    transform: translate(0);
    text-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
  }
  45% {
    transform: translate(0);
    text-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
  }
  47% {
    transform: translate(-2px, 1px);
    text-shadow: -3px 0 2px rgba(100, 182, 255, 0.8), 3px 0 2px rgba(0, 255, 255, 0.8);
  }
  49% {
    transform: translate(2px, -1px);
    text-shadow: 3px 0 2px rgba(100, 182, 255, 0.8), -3px 0 2px rgba(0, 255, 255, 0.8);
  }
  50% {
    transform: translate(0);
    text-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
  }
  90% {
    transform: translate(0);
    text-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
  }
  92% {
    transform: translate(-2px, 1px);
    text-shadow: -3px 0 2px rgba(100, 182, 255, 0.8), 3px 0 2px rgba(0, 255, 255, 0.8);
  }
  94% {
    transform: translate(2px, -1px);
    text-shadow: 3px 0 2px rgba(100, 182, 255, 0.8), -3px 0 2px rgba(0, 255, 255, 0.8);
  }
  96% {
    transform: translate(0);
    text-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
  }
`;

const slideInLeft = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideInRight = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  0% {
    transform: translateY(50px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const techReveal = keyframes`
  0% {
    clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
    opacity: 0;
  }
  100% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    opacity: 1;
  }
`;

const dataFlowAnimation = keyframes`
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 200% 100%;
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const techEntrance = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
    filter: brightness(0.5) blur(10px);
  }
  30% {
    opacity: 0.7;
    transform: scale(1.05);
    filter: brightness(1.2) blur(0px);
  }
  40% {
    opacity: 0.9;
    transform: scale(0.98);
  }
  60% {
    transform: scale(1.02);
  }
  100% {
    opacity: 1;
    transform: scale(1);
    filter: brightness(1) blur(0px);
  }
`;

const simplePulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
  100% {
    transform: scale(1);
  }
`;

const logoPulse = keyframes`
  0% {
    transform: scale(1);
    filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
  }
  50% {
    transform: scale(1.05);
    filter: drop-shadow(0 0 15px rgba(255, 255, 255, 0.8));
  }
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
  }
`;

const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(100, 182, 255, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(100, 182, 255, 0.8), 0 0 30px rgba(0, 255, 255, 0.4);
  }
  100% {
    box-shadow: 0 0 5px rgba(100, 182, 255, 0.5);
  }
`;

const revealLine = keyframes`
  0% {
    width: 0;
    opacity: 0;
  }
  100% {
    width: 100%;
    opacity: 1;
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  color: #64B6FF;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: 2px;
  font-size: 1.8rem;
  text-transform: uppercase;
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
  
  &::before {
    content: '//';
    color: rgba(100, 182, 255, 0.5);
    margin-right: 10px;
  }
`;

const ContentPreview = styled.div`
  max-width: 1200px;
  width: 90%;
  margin: 0 auto;
  padding: 60px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  transform-origin: center;
  animation: ${techReveal} 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  animation-delay: 0.3s;
  opacity: 0;
  
  // Escanear efeito
  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      rgba(100, 182, 255, 0), 
      rgba(100, 182, 255, 0.8), 
      rgba(0, 255, 255, 0.8), 
      rgba(100, 182, 255, 0.8), 
      rgba(100, 182, 255, 0)
    );
  }
  
  &::after {
    top: auto;
    bottom: 0;
  }
`;

// Efeito de vértice tecnológico
const CornerEffect = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  border-color: #64B6FF;
  opacity: 0;
  animation: ${fadeInUp} 0.3s forwards;
  z-index: 5;
  
  &.top-left {
    top: -1px;
    left: -1px;
    border-top: 2px solid;
    border-left: 2px solid;
    animation-delay: 1s;
  }
  
  &.top-right {
    top: -1px;
    right: -1px;
    border-top: 2px solid;
    border-right: 2px solid;
    animation-delay: 1.1s;
  }
  
  &.bottom-left {
    bottom: -1px;
    left: -1px;
    border-bottom: 2px solid;
    border-left: 2px solid;
    animation-delay: 1.2s;
  }
  
  &.bottom-right {
    bottom: -1px;
    right: -1px;
    border-bottom: 2px solid;
    border-right: 2px solid;
    animation-delay: 1.3s;
  }
`;

const StyledDescriptionSection = styled(DescriptionSection)`
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeInUp} 0.8s forwards;
  animation-delay: 1.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledDescriptionText = styled(DescriptionText)`
  opacity: 1 !important;
  transform: translateY(0) !important;
  font-size: 2rem !important;
`;

const AnimatedHighlightedWord = styled(HighlightedWord)`
  display: inline-block;
  animation: ${glitchEffect} 5s infinite;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  transform: scale(0.8);
  
  ${props => css`
    animation: ${glitchEffect} 5s infinite, ${fadeInUp} 0.5s forwards ${props.delay};
  `}
`;

const ScannerLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(to right, 
    rgba(100, 182, 255, 0), 
    rgba(100, 182, 255, 0.5), 
    rgba(0, 255, 255, 0.8), 
    rgba(100, 182, 255, 0.5), 
    rgba(100, 182, 255, 0)
  );
  box-shadow: 0 0 15px 3px rgba(100, 182, 255, 0.5);
  z-index: 10;
  animation: scanDown 2.5s ease-in-out 0.8s forwards;
  opacity: 0;
  
  @keyframes scanDown {
    0% {
      opacity: 0;
      top: 0;
    }
    5% {
      opacity: 1;
    }
    95% {
      opacity: 1;
    }
    100% {
      opacity: 0;
      top: 100%;
    }
  }
`;

const PartnerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.isVisible ? '25px 35px' : '0'};
  background: rgba(0, 10, 30, 0.7);
  border-radius: 10px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: hidden;
  margin-top: ${props => props.isVisible ? '30px' : '0'};
  width: 85%;
  max-width: 600px;
  opacity: 0;
  transform: scale(0.8);
  height: ${props => props.isVisible ? 'auto' : '0'};
  transition: height 0.3s ease, padding 0.3s ease, margin 0.3s ease;
  
  ${props => props.isVisible && css`
    animation: ${techEntrance} 1.2s cubic-bezier(0.215, 0.610, 0.355, 1.000) forwards;
  `}
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      rgba(100, 182, 255, 0), 
      rgba(100, 182, 255, 0.8), 
      rgba(0, 255, 255, 0.8), 
      rgba(100, 182, 255, 0.8), 
      rgba(100, 182, 255, 0)
    );
    opacity: ${props => props.isVisible ? 1 : 0};
    transition: opacity 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      rgba(100, 182, 255, 0), 
      rgba(100, 182, 255, 0.8), 
      rgba(0, 255, 255, 0.8), 
      rgba(100, 182, 255, 0.8), 
      rgba(100, 182, 255, 0)
    );
    opacity: ${props => props.isVisible ? 1 : 0};
    transition: opacity 0.3s ease;
  }
`;

const LogoImage = styled.img`
  height: 70px;
  width: auto;
  margin-right: 30px;
  animation: ${simplePulse} 4s infinite ease-in-out;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const LogoText = styled.div`
  font-family: 'Orbitron', sans-serif;
  color: #fff;
  font-size: 1.4rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  text-shadow: 0 0 10px rgba(100, 182, 255, 0.7);
  background: linear-gradient(90deg, #ffffff, #64B6FF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-size: 200% auto;
  animation: ${gradientShift} 5s ease infinite;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const CornerAccent = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  
  &.top-left {
    top: -1px;
    left: -1px;
    border-top: 2px solid #64B6FF;
    border-left: 2px solid #64B6FF;
  }
  
  &.top-right {
    top: -1px;
    right: -1px;
    border-top: 2px solid #64B6FF;
    border-right: 2px solid #64B6FF;
  }
  
  &.bottom-left {
    bottom: -1px;
    left: -1px;
    border-bottom: 2px solid #64B6FF;
    border-left: 2px solid #64B6FF;
  }
  
  &.bottom-right {
    bottom: -1px;
    right: -1px;
    border-bottom: 2px solid #64B6FF;
    border-right: 2px solid #64B6FF;
  }
`;

const PresentationSlide3 = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDirecional, setShowDirecional] = useState(false);
  
  useEffect(() => {
    // Simular um atraso antes de mostrar o conteúdo
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 200);
    
    return () => clearTimeout(timer);
  }, []);

  // Gerar partículas de energia aleatórias
  const renderEnergyParticles = () => {
    const particles = [];
    const colors = ['#00FFFF', '#64B6FF', '#0080FF', '#FFFFFF'];
    const sizes = ['4px', '6px', '8px', '10px'];
    
    for (let i = 0; i < 20; i++) {
      const top = `${Math.random() * 100}%`;
      const left = `${Math.random() * 100}%`;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = sizes[Math.floor(Math.random() * sizes.length)];
      const duration = `${2 + Math.random() * 3}s`;
      const delay = `${Math.random() * 2}s`;
      
      particles.push(
        <EnergyParticle 
          key={i}
          top={top}
          left={left}
          color={color}
          size={size}
          duration={duration}
          delay={delay}
        />
      );
    }
    
    return particles;
  };

  const handleSlideClick = () => {
    if (!showDirecional) {
      setShowDirecional(true);
    }
  };

  return (
    <SlideWrapper onClick={handleSlideClick}>
      <SlideTitle>O Que é BIANCA</SlideTitle>
      <ScannerLine />
      
      <ContentPreview>
        <CornerEffect className="top-left" />
        <CornerEffect className="top-right" />
        <CornerEffect className="bottom-left" />
        <CornerEffect className="bottom-right" />
        
        <StyledDescriptionSection>
          <EnergyParticles>
            {renderEnergyParticles()}
          </EnergyParticles>
          {/* Removendo o círculo de energia que fica pulsando */}
          <StyledDescriptionText>
            BIANCA é uma agente de IA especializada em{' '}
            <AnimatedHighlightedWord delay="1.5s">
              maximizar
            </AnimatedHighlightedWord>{' '}
            a conversão de{' '}
            <AnimatedHighlightedWord delay="1.7s">
              leads
            </AnimatedHighlightedWord>{' '}
            em{' '}
            <AnimatedHighlightedWord delay="1.9s">
              agendamentos
            </AnimatedHighlightedWord>
          </StyledDescriptionText>
          
          <PartnerContent isVisible={showDirecional}>
            <CornerAccent className="top-left" />
            <CornerAccent className="top-right" />
            <CornerAccent className="bottom-left" />
            <CornerAccent className="bottom-right" />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                <LogoImage 
                  src="/direcional_logo.png" 
                  alt="Direcional" 
                  isVisible={showDirecional} 
                  style={{ marginRight: '15px', height: '60px' }} 
                />
                <LogoText 
                  isVisible={showDirecional} 
                  style={{ 
                    fontSize: '1.6rem', 
                    textShadow: '0 0 15px rgba(100, 182, 255, 0.9)' 
                  }}
                >
                  Direcional Engenharia
                </LogoText>
              </div>
              <div
                style={{
                  fontSize: '1.2rem',
                  letterSpacing: '1px',
                  color: '#64B6FF',
                  textShadow: '0 0 8px rgba(100, 182, 255, 0.6)',
                  marginTop: '8px',
                  fontFamily: "'Orbitron', sans-serif",
                  fontWeight: '400',
                  opacity: showDirecional ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }}
              >
                Uma das Maiores construtoras do Brasil
              </div>
            </div>
          </PartnerContent>
        </StyledDescriptionSection>
      </ContentPreview>
    </SlideWrapper>
  );
};

export default PresentationSlide3;
